import { memo } from 'react';
import { Typography } from '../../../../../components/typography/typography';
import Icon from '../../../../../components/icons/icon';
import { DateTimeCell } from '../../../order-table-cells';
import { DateTimePicker } from '../../../../../components/date-picker/date-time-picker';
import { startEndDateHours } from '../../../../../hooks/use-location-interval-helpers';
import { Location } from '../../../../../clients/quipli-service/location/location';
import { ProductItemState } from '../../hooks/use-product-item-data';
import deepEqual from 'deep-equal';
import { ProductItemDatesReturn } from '../../hooks/use-product-item-dates';

interface ProductItemDatesProps
  extends Pick<ProductItemState, 'isRental' | 'isEditing'>,
    Omit<ProductItemDatesReturn, 'isEndDateInputValid'> {
  location: Location;
  onlyShowStoreHoursOnDateTimePicker: boolean;
  showOverbookWarning: boolean;
  isOrderCycleBilled: boolean;
}

export const ProductItemDates = memo<ProductItemDatesProps>(
  ({
    isRental,
    isEditing,
    location,
    onlyShowStoreHoursOnDateTimePicker,
    showOverbookWarning,
    isOrderCycleBilled,

    startDateInput,
    endDateInput,
    updateStartDate,
    updateEndDate,
  }) => {
    // Calculate date intervals for date pickers
    const { startDaysHours, endDaysHours } = startEndDateHours(
      startDateInput,
      endDateInput,
      location,
      onlyShowStoreHoursOnDateTimePicker,
    );

    if (!isRental) {
      return (
        <Typography type="text-sm" weight="regular" className="text-quipli-black-800" dataCy="product-item-dates-na">
          N/A
        </Typography>
      );
    }

    return (
      <>
        <div className="ml-[2px] min-w-full flex gap-2 items-center">
          <div className="flex-[0_1_50%]">
            {isEditing ? (
              <DateTimePicker
                value={startDateInput}
                onChange={(date) => {
                  date && updateStartDate(date);
                }}
                minTime={startDaysHours?.start}
                maxTime={startDaysHours?.end}
                timezone={location.timezone}
                usePortal={true}
                customDisplay={<DateTimeCell date={startDateInput} isEdit={true} />}
              />
            ) : (
              <DateTimeCell date={startDateInput} />
            )}
          </div>
          <div className="flex-[0_1_50%]">
            {isEditing ? (
              <DateTimePicker
                value={endDateInput}
                onChange={(date) => {
                  updateEndDate(date);
                }}
                minTime={endDaysHours?.start}
                maxTime={endDaysHours?.end}
                timezone={location.timezone}
                usePortal={true}
                customDisplay={
                  (endDateInput && <DateTimeCell date={endDateInput} isEdit={true} />) || <span>Not set</span>
                }
                customErrorDisplay={<Icon icon="CircleExclamation" size="small" />}
                clearable={isOrderCycleBilled}
              />
            ) : (
              endDateInput && <DateTimeCell date={endDateInput} />
            )}
          </div>
        </div>

        {isRental && showOverbookWarning && (
          <Typography
            type="text-sm"
            weight="semi-bold"
            className="!text-error-700 py-4"
            dataCy="product-item-dates-overbook-warning"
          >
            Warning: Another order is already scheduled for these dates.
          </Typography>
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for React.memo
    return (
      prevProps.isRental === nextProps.isRental &&
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.startDateInput.equals(nextProps.startDateInput) &&
      (prevProps.endDateInput && nextProps.endDateInput
        ? prevProps.endDateInput.equals(nextProps.endDateInput)
        : prevProps.endDateInput === nextProps.endDateInput) &&
      prevProps.showOverbookWarning === nextProps.showOverbookWarning &&
      prevProps.onlyShowStoreHoursOnDateTimePicker === nextProps.onlyShowStoreHoursOnDateTimePicker &&
      prevProps.isOrderCycleBilled === nextProps.isOrderCycleBilled &&
      deepEqual(prevProps.location, nextProps.location)
    );
  },
);

ProductItemDates.displayName = 'ProductItemDates';
